import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, Download, Search, Trash2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'sage_converter_state';

// Inline editable cell component
function EditableCell({ value, onSave, placeholder, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const save = () => {
    setEditing(false);
    if (draft.trim() !== (value || '').trim()) {
      onSave(draft.trim());
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        className={`w-full px-1 py-0.5 border border-emerald-400 rounded text-xs outline-none bg-white ${className}`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      className={`cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded inline-block min-w-[40px] min-h-[18px] ${!value ? 'text-slate-300 italic' : ''} ${className}`}
      title="Click to edit"
    >
      {value || placeholder || '—'}
    </span>
  );
}

export default function SageConverter() {
  // Restore state from sessionStorage on mount
  const [parsedData, setParsedData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        return state.parsedData || null;
      }
    } catch { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [search, setSearch] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [setQty, setSetQty] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        return state.setQty || 1;
      }
    } catch { /* ignore */ }
    return 1;
  });
  const fileRef = useRef(null);
  const [vendorConflicts, setVendorConflicts] = useState(null);

  // Persist parsedData & setQty to sessionStorage
  useEffect(() => {
    if (parsedData) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ parsedData, setQty }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [parsedData, setQty]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['xls', 'xlsx'].includes(ext)) {
      toast.error('Please select an .xls or .xlsx file');
      return;
    }
    setUploadedFile(file);
    setLoading(true);
    setParsedData(null);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await axios.post(`${API}/api/warehouse/sage-convert/parse`, fd);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setParsedData(res.data);
        setSetQty(res.data.set_qty || 1);
        toast.success(`Parsed: ${res.data.component_count} components (${res.data.format_detected} format)`);
      }
    } catch (err) {
      toast.error('Failed to parse file');
    }
    setLoading(false);
  };

  const handleConvert = async (vendorChoices = null) => {
    if (!parsedData) return;

    // Detect conflicts when not already resolved
    if (vendorChoices === null) {
      const conflicts = (parsedData.components || []).filter(c => c.vendor_conflict);
      if (conflicts.length > 0) {
        setVendorConflicts(conflicts);
        return;  // User must resolve via modal
      }
    }

    setConverting(true);

    try {
      let res;
      const overridesPayload = vendorChoices ? JSON.stringify(vendorChoices) : '';
      if (uploadedFile) {
        // Original file available - use file-based convert
        const fd = new FormData();
        fd.append('file', uploadedFile);
        fd.append('set_qty', setQty);
        if (overridesPayload) fd.append('vendor_overrides', overridesPayload);
        res = await axios.post(`${API}/api/warehouse/sage-convert/convert`, fd, {
          responseType: 'blob',
        });
      } else {
        // No file (page refreshed) - use parsed data from state
        res = await axios.post(`${API}/api/warehouse/sage-convert/convert-from-data`,
          { ...parsedData, set_qty: setQty, vendor_overrides: vendorChoices || {} },
          { responseType: 'blob' }
        );
      }
      const contentDisposition = res.headers['content-disposition'];
      let filename = 'converted.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${filename}`);
      setVendorConflicts(null);
    } catch (err) {
      toast.error('Conversion failed');
    }
    setConverting(false);
  };

  const handleReset = () => {
    setParsedData(null);
    setUploadedFile(null);
    setSearch('');
    setSetQty(1);
    sessionStorage.removeItem(STORAGE_KEY);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Re-select file for download (data already in state from sessionStorage)
  const handleReUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    toast.success('File re-selected. You can now download the Excel.');
  };

  // Save vendor/group edit to DB and update local state
  const handleFieldSave = useCallback(async (itemCode, field, value) => {
    try {
      await axios.put(`${API}/api/warehouse/sage-convert/components/update`, {
        item_code: itemCode,
        [field]: value,
      });
      // Update local parsed data
      setParsedData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          components: prev.components.map(c =>
            c.item_code === itemCode ? { ...c, [field]: value } : c
          ),
        };
      });
      toast.success(`Saved: ${itemCode} → ${field} = "${value}"`);
    } catch (err) {
      toast.error('Failed to save');
    }
  }, []);

  // Filtered and calculated items
  const displayItems = useMemo(() => {
    if (!parsedData?.components) return [];
    return parsedData.components
      .filter(item => {
        if (!search) return true;
        const s = search.toLowerCase();
        return item.item_code.toLowerCase().includes(s) ||
          item.description.toLowerCase().includes(s) ||
          (item.vendor || '').toLowerCase().includes(s) ||
          (item.group_name || '').toLowerCase().includes(s);
      })
      .map(item => {
        const required = item.qty_per_bill * setQty;
        const short = Math.max(0, required - item.qty_available);
        let availLevel = 'none';
        let shortLevel = 'none';
        if (required > 0) {
          if (item.qty_available <= 0) { availLevel = 'red'; shortLevel = 'red'; }
          else if (item.qty_available < required) { availLevel = 'orange'; shortLevel = 'orange'; }
          else if (item.qty_available < required * 2) { availLevel = 'yellow'; shortLevel = 'green'; }
          else { availLevel = 'green'; shortLevel = 'green'; }
        }
        return { ...item, required, short, availLevel, shortLevel };
      });
  }, [parsedData, search, setQty]);

  const levelStyles = {
    red: 'bg-red-100 text-red-800 font-bold',
    orange: 'bg-orange-100 text-orange-800 font-bold',
    yellow: 'bg-yellow-50 text-yellow-800',
    green: 'bg-emerald-50 text-emerald-800',
    none: '',
  };

  return (
    <div className="space-y-5">
      {!parsedData ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6">
            <RefreshCw className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Component Requirement Converter</h2>
          <p className="text-sm text-slate-500 mb-8 text-center max-w-md">
            Upload a raw Sage Component Requirement Report (.xls / .xlsx). The system remembers vendor &amp; group assignments permanently — edit once, auto-fill forever.
          </p>
          <label className="cursor-pointer flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-semibold text-sm shadow-lg shadow-emerald-200"
            data-testid="sage-upload-btn">
            <Upload className="w-4 h-4" />
            {loading ? 'Parsing...' : 'Upload Sage File (.xls / .xlsx)'}
            <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleUpload} disabled={loading} />
          </label>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview Header */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                Parsed File Preview
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                  {parsedData.format_detected} format
                </span>
                <button onClick={handleReset} className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid="sage-reset-btn">
                  <Trash2 className="w-3 h-3" /> Reset
                </button>
              </div>
            </div>

            {/* Bill Info */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Bill Number</div>
                <div className="text-sm font-bold text-slate-800" data-testid="sage-bill-number">{parsedData.bill_number}</div>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">SR Description</div>
                <div className="text-sm font-bold text-slate-800 truncate" data-testid="sage-sr-desc">{parsedData.sr_description}</div>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Components</div>
                <div className="text-sm font-bold text-slate-800" data-testid="sage-comp-count">{parsedData.component_count}</div>
              </div>
              <div className="bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                <div className="text-[10px] text-amber-700 uppercase font-semibold">Set Quantity</div>
                <input type="number" min={1} value={setQty}
                  onChange={e => setSetQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full text-lg font-bold text-amber-800 bg-transparent border-none outline-none p-0"
                  data-testid="sage-set-qty" />
              </div>
            </div>

            {/* Hint + Convert */}
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => handleConvert()} disabled={converting || !parsedData}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-semibold text-sm shadow-sm disabled:opacity-50"
                data-testid="sage-convert-btn">
                <Download className="w-4 h-4" />
                {converting ? 'Converting...' : 'Convert & Download Excel'}
              </button>
              <span className="text-[11px] text-slate-400">
                Click VENDOR or Group cells to edit — changes are saved permanently
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by item code, description, vendor or group..."
              className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
              data-testid="sage-search" />
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sage-components-table">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="px-2 py-2.5 text-center font-semibold w-12">Occur</th>
                    <th className="px-2 py-2.5 text-left font-semibold w-28">Item Code</th>
                    <th className="px-2 py-2.5 text-left font-semibold">Description</th>
                    <th className="px-2 py-2.5 text-left font-semibold w-40">
                      VENDOR <span className="text-emerald-300 text-[9px] font-normal ml-1">editable</span>
                    </th>
                    <th className="px-2 py-2.5 text-left font-semibold w-28">
                      Group <span className="text-emerald-300 text-[9px] font-normal ml-1">editable</span>
                    </th>
                    <th className="px-2 py-2.5 text-center font-semibold w-14">UoM</th>
                    <th className="px-2 py-2.5 text-center font-semibold w-16">Per Bill</th>
                    <th className="px-2 py-2.5 text-center font-semibold w-20">Required</th>
                    <th className="px-2 py-2.5 text-center font-semibold w-20">Available</th>
                    <th className="px-2 py-2.5 text-center font-semibold w-16">Short</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, idx) => (
                    <tr key={`${item.item_code}-${idx}`}
                      className={`border-b border-slate-100 hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-2 py-1.5 text-center text-slate-500">{item.occur}</td>
                      <td className="px-2 py-1.5 font-mono font-bold text-slate-800">{item.item_code}</td>
                      <td className="px-2 py-1.5 text-slate-700 truncate max-w-[260px]">{item.description}</td>
                      <td className="px-2 py-0.5">
                        <EditableCell
                          value={item.vendor}
                          onSave={val => handleFieldSave(item.item_code, 'vendor', val)}
                          placeholder="click to set"
                        />
                      </td>
                      <td className="px-2 py-0.5">
                        <EditableCell
                          value={item.group_name}
                          onSave={val => handleFieldSave(item.item_code, 'group_name', val)}
                          placeholder="click to set"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center text-slate-500">{item.uom}</td>
                      <td className="px-2 py-1.5 text-center font-semibold">{item.qty_per_bill}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-blue-700">{item.required}</td>
                      <td className={`px-2 py-1.5 text-center ${levelStyles[item.availLevel]}`}>{item.qty_available}</td>
                      <td className={`px-2 py-1.5 text-center ${levelStyles[item.shortLevel]}`}>
                        {item.short > 0 ? item.short : 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {displayItems.length > 0 && (
                  <tfoot>
                    {(() => {
                      const byUnit = {};
                      displayItems.forEach(i => {
                        const u = i.uom || 'EACH';
                        if (!byUnit[u]) byUnit[u] = { required: 0, short: 0, count: 0 };
                        byUnit[u].required += i.required;
                        byUnit[u].short += i.short > 0 ? i.short : 0;
                        byUnit[u].count++;
                      });
                      const units = Object.keys(byUnit).sort();
                      return units.map((u, idx) => (
                        <tr key={u} className={idx === 0 ? "bg-slate-100 border-t-2 border-slate-300" : "bg-slate-50 border-t border-slate-200"}>
                          <td className="px-2 py-2 font-bold text-slate-700 text-center" colSpan={2}>{idx === 0 ? 'TOTAL' : ''}</td>
                          <td className="px-2 py-2 font-semibold text-slate-600">{byUnit[u].count} items</td>
                          <td colSpan={3}></td>
                          <td className="px-2 py-2 text-center font-semibold text-slate-500">{u}</td>
                          <td className="px-2 py-2 text-center font-bold text-blue-700">{byUnit[u].required}</td>
                          <td></td>
                          <td className="px-2 py-2 text-center font-bold text-red-600">{byUnit[u].short > 0 ? byUnit[u].short : '-'}</td>
                        </tr>
                      ));
                    })()}
                  </tfoot>
                )}
              </table>
            </div>
          </div>

        </div>
      )}

      <VendorConflictModal
        conflicts={vendorConflicts}
        onClose={() => setVendorConflicts(null)}
        onResolve={(choices) => handleConvert(choices)}
        converting={converting}
      />
    </div>
  );
}

function VendorConflictModal({ conflicts, onClose, onResolve, converting }) {
  const [choices, setChoices] = useState({});
  const [bulkChoice, setBulkChoice] = useState('file');  // 'file' | 'db'

  useEffect(() => {
    if (!conflicts) return;
    const init = {};
    conflicts.forEach(c => { init[c.item_code] = c.file_vendor || c.db_vendor; });
    setChoices(init);
  }, [conflicts]);

  if (!conflicts || conflicts.length === 0) return null;

  const applyBulk = (target) => {
    const next = {};
    conflicts.forEach(c => { next[c.item_code] = target === 'file' ? (c.file_vendor || c.db_vendor) : (c.db_vendor || c.file_vendor); });
    setChoices(next);
    setBulkChoice(target);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="vendor-conflict-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
        <div className="px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-600 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-100 font-semibold">Vendor Mismatch Detected</div>
            <div className="text-lg font-bold text-white">Resolve {conflicts.length} vendor conflicts</div>
          </div>
          <button onClick={onClose} className="text-amber-100 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
          The new Sage report contains vendor names that differ from your component database.
          Choose which value to keep for each item — your choice will overwrite the DB record.
        </div>

        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-xs">
          <span className="font-semibold text-slate-600">Bulk action:</span>
          <button onClick={() => applyBulk('file')}
            className={`px-3 py-1.5 rounded-lg font-semibold ${bulkChoice === 'file' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
            data-testid="bulk-use-file">
            Use ALL "File" values
          </button>
          <button onClick={() => applyBulk('db')}
            className={`px-3 py-1.5 rounded-lg font-semibold ${bulkChoice === 'db' ? 'bg-purple-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
            data-testid="bulk-use-db">
            Use ALL "DB" values
          </button>
          <span className="text-slate-400 ml-2">or pick individually below</span>
        </div>

        <div className="flex-1 overflow-auto px-6 py-3">
          <table className="text-xs w-full" data-testid="conflict-table">
            <thead className="sticky top-0 bg-white shadow-sm">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                <th className="px-2 py-2">Item Code</th>
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2">Existing in DB</th>
                <th className="px-2 py-2">From File</th>
                <th className="px-2 py-2 w-32">Choose</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c, i) => {
                const chosen = choices[c.item_code];
                return (
                  <tr key={c.item_code} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-2 py-2 font-mono font-semibold text-slate-700">{c.item_code}</td>
                    <td className="px-2 py-2 text-slate-600 max-w-[200px] truncate" title={c.description}>{c.description}</td>
                    <td className="px-2 py-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name={`v-${c.item_code}`}
                          checked={chosen === c.db_vendor}
                          onChange={() => setChoices(s => ({ ...s, [c.item_code]: c.db_vendor }))}
                          className="mt-0.5"
                          data-testid={`db-${c.item_code}`} />
                        <span className={`${chosen === c.db_vendor ? 'font-bold text-purple-700' : 'text-slate-700'}`}>{c.db_vendor || '—'}</span>
                      </label>
                    </td>
                    <td className="px-2 py-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name={`v-${c.item_code}`}
                          checked={chosen === c.file_vendor}
                          onChange={() => setChoices(s => ({ ...s, [c.item_code]: c.file_vendor }))}
                          className="mt-0.5"
                          data-testid={`file-${c.item_code}`} />
                        <span className={`${chosen === c.file_vendor ? 'font-bold text-blue-700' : 'text-slate-700'}`}>{c.file_vendor || '—'}</span>
                      </label>
                    </td>
                    <td className="px-2 py-2">
                      <input type="text" value={chosen || ''}
                        onChange={e => setChoices(s => ({ ...s, [c.item_code]: e.target.value }))}
                        placeholder="Custom…"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <div className="text-[11px] text-slate-500">
            <span className="font-bold text-slate-700">{Object.values(choices).filter(Boolean).length}</span> / {conflicts.length} resolved
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800">Cancel</button>
            <button onClick={() => onResolve(choices)} disabled={converting}
              className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60"
              data-testid="apply-conflicts">
              {converting ? 'Converting…' : 'Apply & Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
